import { lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import AdminRoute from '@/components/common/AdminRoute';
import BillingRoute from '@/components/common/BillingRoute';
import GuestRoute from '@/components/common/GuestRoute';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import SalesRmRoute from '@/components/common/SalesRmRoute';
import ServiceRoute from '@/components/common/ServiceRoute';
import StaffRoute from '@/components/common/StaffRoute';
import AdminLayout from '@/components/layout/AdminLayout';
import AuthLayout from '@/components/layout/AuthLayout';
import BillingLayout from '@/components/layout/BillingLayout';
import MainLayout from '@/components/layout/MainLayout';
import MisLayout from '@/components/layout/MisLayout';
import SalesLayout from '@/components/layout/SalesLayout';
import ServiceLayout from '@/components/layout/ServiceLayout';
import AdminCompanySettingsPage from '@/pages/AdminCompanySettingsPage';
import AdminCustomCitiesPage from '@/pages/AdminCustomCitiesPage';
import AdminDevPage from '@/pages/AdminDevPage';
import AdminEmailTemplateEditorPage from '@/pages/AdminEmailTemplateEditorPage';
import AdminEmailTemplatesPage from '@/pages/AdminEmailTemplatesPage';
import AdminMigrationPage from '@/pages/AdminMigrationPage';
import AdminOverviewPage from '@/pages/AdminOverviewPage';
import AdminPincodesPage from '@/pages/AdminPincodesPage';
import AdminPriceSettingsPage from '@/pages/AdminProductSettingsPage';
import AdminSessionsPage from '@/pages/AdminSessionsPage';
import AdminSlabsPage from '@/pages/AdminSlabsPage';
import AdminUserDetailPage from '@/pages/AdminUserDetailPage';
import AdminUsersPage from '@/pages/AdminUsersPage';
import ApprovalsPage from '@/pages/ApprovalsPage';
import BillingClientsPage from '@/pages/BillingClientsPage';
import BillingDashboardPage from '@/pages/BillingDashboardPage';
import BillingWorkflowPage from '@/pages/BillingWorkflowPage';
import EmailLogsPage from '@/pages/EmailLogsPage';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import NoWorkspacePage from '@/pages/NoWorkspacePage';
import NotFoundPage from '@/pages/NotFoundPage';
import OnboardingPage from '@/pages/OnboardingPage';
import ProfilePage from '@/pages/ProfilePage';
import RegisterPage from '@/pages/RegisterPage';
import RenewalDashboardPage from '@/pages/RenewalDashboardPage';
import RenewalWorkflowPage from '@/pages/RenewalWorkflowPage';
import RenewalsPage from '@/pages/RenewalsPage';
import SalesHomePage from '@/pages/SalesHomePage';
import SalesLeadsPage from '@/pages/SalesLeadsPage';
import SalesOrdersPage from '@/pages/SalesOrdersPage';
import ServicesPage from '@/pages/ServicesPage';
import TcDocumentsPage from '@/pages/TcDocumentsPage';
import PublicTcPage from '@/pages/public/PublicTcPage';
import ReviewPage from '@/pages/public/ReviewPage';
import { ROUTES } from '@/utils/constants';

/**
 * The four MIS boards are LAZY, and that is a data decision as much as a bundle
 * one.
 *
 * Each board fires its own aggregation queries the moment it mounts. Static
 * imports put all four in the main bundle, so every admin paid to download four
 * analytics boards to open the Approvals queue. Lazy chunks mean a board's code -
 * and therefore its queries - only exist once someone selects that tab.
 *
 * `MisLayout` renders the `<Suspense>` boundary these need.
 *
 * These are the staging modules (MIS_work_1 / MIS_work_2). They must never
 * import each other; naming both here is fine, because this file is the shared
 * router, not either module.
 */
const MisAnalyticsPage = lazy(() => import('@/MIS_work_1_frontend/MisAnalyticsPage'));
const MisRenewalsPage = lazy(() => import('@/MIS_work_1_frontend/MisRenewalsPage'));
const MisForecastPage = lazy(() => import('@/MIS_work_2_frontend/MisForecastPage'));
const MisMfdPage = lazy(() => import('@/MIS_work_2_frontend/mfd/MisMfdPage'));

const AppRouter = () => (
  <BrowserRouter>
    <Routes>
      {/* Public marketing page, accessible regardless of auth state */}
      <Route path={ROUTES.LANDING} element={<LandingPage />} />

      {/* Public page reached from lead emails (no auth required) */}
      <Route path={ROUTES.TC_PUBLIC} element={<PublicTcPage />} />

      {/* DEV ONLY. The lead review page is a link-only view of a lead, built
          from a snapshot in the query string, and nothing in production links to
          it: no email template references /review/, and its one entry point (the
          "Lead" button in wizard step 3) is dev-gated too. `import.meta.env.DEV`
          is a build-time constant, so the route and the page's chunk are both
          dropped from a production bundle rather than merely hidden. */}
      {import.meta.env.DEV && <Route path={ROUTES.REVIEW} element={<ReviewPage />} />}

      {/* Guest-only routes, redirect to dashboard if already logged in */}
      <Route element={<GuestRoute />}>
        <Route element={<AuthLayout />}>
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />
          <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
        </Route>
      </Route>

      {/* Protected routes, redirect to login if not authenticated */}
      <Route element={<ProtectedRoute />}>
        {/* Forced one-time onboarding - standalone, no sidebar */}
        <Route path={ROUTES.ONBOARDING} element={<OnboardingPage />} />

        {/* An approved account whose jobRole opens no workspace - standalone,
            no sidebar, since there is no nav to show it. */}
        <Route path={ROUTES.NO_WORKSPACE} element={<NoWorkspacePage />} />

        <Route element={<MainLayout />}>
          <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
        </Route>

        {/* Admin console: admin-only pages */}
        <Route element={<AdminRoute />}>
          <Route path={ROUTES.ADMIN} element={<AdminLayout />}>
            <Route index element={<AdminOverviewPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:id" element={<AdminUserDetailPage />} />
            <Route path="sessions" element={<AdminSessionsPage />} />
            <Route path="pincodes" element={<AdminPincodesPage />} />
            <Route path="custom-cities" element={<AdminCustomCitiesPage />} />
            <Route path="company-settings" element={<AdminCompanySettingsPage />} />
            <Route path="price-settings" element={<AdminPriceSettingsPage />} />
            <Route path="slabs" element={<AdminSlabsPage />} />
            <Route path="migration" element={<AdminMigrationPage />} />
            {/* Dev-only, like its sidebar entry. `import.meta.env.DEV` is a
                build-time constant, so the route and the page's chunk are both
                dropped from a production bundle rather than just hidden. The
                server refuses these resets outside development regardless. */}
            {import.meta.env.DEV && <Route path="dev" element={<AdminDevPage />} />}
            <Route path="email-templates/new" element={<AdminEmailTemplateEditorPage />} />
            <Route path="email-templates/:id/edit" element={<AdminEmailTemplateEditorPage />} />
            <Route
              path="email-logs"
              element={<EmailLogsPage storageKey="admin-email-logs-filters" />}
            />
            <Route path="tc-documents" element={<TcDocumentsPage />} />
            {/* The four MIS boards, admin-only, sharing one shell.
                A PATHLESS layout route: it contributes no URL segment, so every
                board keeps the exact path it had and only gains the segmented
                control above it. Exactly one board is mounted at a time, which
                is what stops four sets of MIS queries firing at once. */}
            <Route element={<MisLayout />}>
              <Route path="mis" element={<MisAnalyticsPage />} />
              <Route path="mis-renewals" element={<MisRenewalsPage />} />
              <Route path="mis-forecast" element={<MisForecastPage />} />
              <Route path="mis-mfd" element={<MisMfdPage />} />
            </Route>
          </Route>
        </Route>

        {/* Staff pages (admin + Sales RM): email templates are read-only for Sales RM,
            create/edit/delete is admin-only (see AdminRoute above) */}
        <Route element={<StaffRoute />}>
          <Route path={ROUTES.ADMIN} element={<AdminLayout />}>
            <Route path="profile" element={<ProfilePage />} />
            <Route path="email-templates" element={<AdminEmailTemplatesPage />} />
          </Route>
        </Route>

        {/* Billing workspace: requires the Billing jobRole (admins may also view) */}
        <Route element={<BillingRoute />}>
          <Route element={<BillingLayout />}>
            <Route path={ROUTES.BILLING_DASHBOARD} element={<BillingDashboardPage />} />
            <Route path={ROUTES.BILLING_CLIENTS} element={<BillingClientsPage />} />
            <Route path="/billing/clients/:id" element={<BillingWorkflowPage />} />
            <Route path={ROUTES.RENEWALS} element={<RenewalsPage />} />
            <Route path="/billing/renewals/:id" element={<RenewalWorkflowPage />} />
            <Route path={ROUTES.RENEWAL_DASHBOARD} element={<RenewalDashboardPage />} />
            <Route
              path={ROUTES.BILLING_EMAIL_LOGS}
              element={<EmailLogsPage storageKey="billing-email-logs-filters" />}
            />
            <Route path={ROUTES.BILLING_PROFILE} element={<ProfilePage />} />
          </Route>
        </Route>

        {/* Service workspace: requires the Service jobRole (admins may also view) */}
        <Route element={<ServiceRoute />}>
          <Route element={<ServiceLayout />}>
            <Route path={ROUTES.SERVICE} element={<ServicesPage />} />
            <Route path={ROUTES.SERVICE_PROFILE} element={<ProfilePage />} />
          </Route>
        </Route>

        {/* Sales RM workspace: requires the Sales RM jobRole (admins may also view) */}
        <Route element={<SalesRmRoute />}>
          <Route element={<SalesLayout />}>
            <Route path={ROUTES.SALES_RM_HOME} element={<SalesHomePage />} />
            <Route path={ROUTES.SALES_RM_LEADS} element={<SalesLeadsPage />} />
            {/* The open order lives in the URL (ADR-048) so the breadcrumb's
                "Sales" crumb is a real navigation back to the list. */}
            <Route path={ROUTES.SALES_RM_ORDERS} element={<SalesOrdersPage />} />
            <Route path={`${ROUTES.SALES_RM_ORDERS}/:orderId`} element={<SalesOrdersPage />} />
            <Route
              path={ROUTES.SALES_RM_EMAIL_LOGS}
              element={<EmailLogsPage storageKey="sales-email-logs-filters" />}
            />
            <Route path={ROUTES.SALES_RM_TC_DOCUMENTS} element={<TcDocumentsPage />} />
            <Route path={ROUTES.SALES_RM_PROFILE} element={<ProfilePage />} />
          </Route>
        </Route>
      </Route>

      <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
    </Routes>
  </BrowserRouter>
);

export default AppRouter;
